import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Badge,
  Tooltip,
  Divider,
  Tabs
} from "antd";
import {
  BankOutlined,
  PlusOutlined,
  DollarCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CheckOutlined,
  SendOutlined
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const SalaryAccountsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [salaryAccounts, setSalaryAccounts] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);

  // Modals
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [creditModalVisible, setCreditModalVisible] = useState(false);

  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedPendingPayment, setSelectedPendingPayment] = useState(null);

  const [accountForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [creditForm] = Form.useForm();

  useEffect(() => {
    fetchSalaryAccounts();
    fetchSalaryPayments();
  }, []);

  const fetchSalaryAccounts = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/salary/accounts");
      const list = res.data?.data || [];
      setSalaryAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching salary accounts:", err);
      // Fallback mock accounts if DB is fresh
      setSalaryAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryPayments = async () => {
    try {
      const res = await apiClient.get("/salary/payments");
      const list = res.data?.data || [];
      setSalaryPayments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching salary payments:", err);
      setSalaryPayments([]);
    }
  };

  // KPI Metrics Calculation
  const totalBasePayroll = salaryAccounts.reduce((sum, a) => sum + (Number(a.baseSalary || 0) + Number(a.allowances || 0)), 0);
  
  const currentMonthStr = dayjs().format("MMMM YYYY");
  
  const creditedThisMonth = salaryPayments
    .filter(p => p.status === "credited")
    .reduce((sum, p) => sum + Number(p.netAmount || 0), 0);

  const pendingPaymentsList = salaryPayments.filter(p => p.status === "pending");
  const pendingTotalAmount = pendingPaymentsList.reduce((sum, p) => sum + Number(p.netAmount || 0), 0);

  // Handle Save Salary Account (Create / Edit)
  const handleSaveAccount = async (values) => {
    try {
      const payload = {
        employeeName: values.employeeName,
        email: values.email,
        phone: values.phone,
        designation: values.designation || "Employee",
        department: values.department || "Operations",
        baseSalary: Number(values.baseSalary || 0),
        allowances: Number(values.allowances || 0),
        bankDetails: {
          bankName: values.bankName || "",
          accountNumber: values.accountNumber || "",
          ifscCode: values.ifscCode || "",
          panNumber: values.panNumber || "",
          upiId: values.upiId || ""
        }
      };

      if (editingAccount) {
        await apiClient.put(`/salary/accounts/${editingAccount._id}`, payload);
        message.success("Employee salary account updated!");
      } else {
        await apiClient.post("/salary/accounts", payload);
        message.success("Employee salary account added!");
      }

      setAccountModalVisible(false);
      setEditingAccount(null);
      accountForm.resetFields();
      fetchSalaryAccounts();
    } catch (err) {
      console.error("Failed to save salary account:", err);
      message.error("Failed to save salary account");
    }
  };

  // Handle Delete Account
  const handleDeleteAccount = async (id) => {
    try {
      await apiClient.delete(`/salary/accounts/${id}`);
      message.success("Salary account deleted");
      fetchSalaryAccounts();
    } catch (err) {
      console.error("Delete failed:", err);
      message.error("Failed to delete account");
    }
  };

  // Handle Record Salary Payment
  const handleRecordPayment = async (values) => {
    try {
      const account = salaryAccounts.find(a => a._id === values.salaryAccountId);

      const baseSalary = Number(values.baseSalary || account?.baseSalary || 0);
      const allowances = Number(values.allowances || account?.allowances || 0);
      const bonus = Number(values.bonus || 0);
      const deductions = Number(values.deductions || 0);

      const payload = {
        salaryAccountId: values.salaryAccountId,
        employeeName: account ? account.employeeName : values.employeeName,
        month: values.month || dayjs().format("MMMM YYYY"),
        baseSalary,
        allowances,
        bonus,
        deductions,
        paymentMode: values.paymentMode || "bank_transfer",
        utrNumber: values.utrNumber || "",
        status: values.status || "credited",
        notes: values.notes || "",
        paymentDate: values.paymentDate ? values.paymentDate.toDate() : new Date()
      };

      await apiClient.post("/salary/payments", payload);
      message.success(values.status === "pending" ? "Salary recorded as Pending!" : "Salary Credited recorded successfully!");
      setPaymentModalVisible(false);
      paymentForm.resetFields();
      fetchSalaryPayments();
    } catch (err) {
      console.error("Record salary failed:", err);
      message.error("Failed to record salary");
    }
  };

  // Quick Credit Pending Salary
  const handleQuickCredit = async (values) => {
    try {
      if (!selectedPendingPayment) return;

      await apiClient.patch(`/salary/payments/${selectedPendingPayment._id}/status`, {
        status: "credited",
        utrNumber: values.utrNumber
      });

      message.success(`Salary credited to ${selectedPendingPayment.employeeName}!`);
      setCreditModalVisible(false);
      setSelectedPendingPayment(null);
      creditForm.resetFields();
      fetchSalaryPayments();
    } catch (err) {
      console.error("Credit update failed:", err);
      message.error("Failed to update salary status");
    }
  };

  const accountColumns = [
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{name}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            {record.designation} • {record.department}
          </div>
        </div>
      )
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          {record.phone && <div>📞 {record.phone}</div>}
          {record.email && <div style={{ color: "#64748b" }}>✉️ {record.email}</div>}
        </div>
      )
    },
    {
      title: "Salary Structure",
      key: "salary",
      render: (_, record) => {
        const base = Number(record.baseSalary || 0);
        const allow = Number(record.allowances || 0);
        const total = base + allow;
        return (
          <div>
            <div style={{ fontWeight: 700, color: "#059669" }}>₹{total.toLocaleString()}/mo</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>
              Base: ₹{base.toLocaleString()} {allow > 0 ? `+ Allow: ₹${allow.toLocaleString()}` : ''}
            </div>
          </div>
        );
      }
    },
    {
      title: "Bank & Account Details",
      key: "bankDetails",
      render: (_, record) => {
        const b = record.bankDetails || {};
        return (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>{b.bankName || 'Bank Not Added'}</div>
            {b.accountNumber && <div>A/C: {b.accountNumber}</div>}
            {b.ifscCode && <div>IFSC: {b.ifscCode}</div>}
            {b.upiId && <Tag color="blue" style={{ marginTop: 2 }}>UPI: {b.upiId}</Tag>}
          </div>
        );
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (st) => (
        <Tag color={st === 'inactive' ? 'red' : 'green'}>
          {(st || 'ACTIVE').toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={() => {
              paymentForm.setFieldsValue({
                salaryAccountId: record._id,
                employeeName: record.employeeName,
                baseSalary: record.baseSalary,
                allowances: record.allowances,
                month: dayjs().format("MMMM YYYY"),
                status: "credited"
              });
              setPaymentModalVisible(true);
            }}
            style={{ background: "#059669", borderColor: "#059669" }}
          >
            Pay Salary
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingAccount(record);
              accountForm.setFieldsValue({
                employeeName: record.employeeName,
                email: record.email,
                phone: record.phone,
                designation: record.designation,
                department: record.department,
                baseSalary: record.baseSalary,
                allowances: record.allowances,
                bankName: record.bankDetails?.bankName,
                accountNumber: record.bankDetails?.accountNumber,
                ifscCode: record.bankDetails?.ifscCode,
                panNumber: record.bankDetails?.panNumber,
                upiId: record.bankDetails?.upiId
              });
              setAccountModalVisible(true);
            }}
          />
          <Popconfirm
            title="Delete this salary account?"
            onConfirm={() => handleDeleteAccount(record._id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const paymentColumns = [
    {
      title: "Month / Date",
      key: "monthDate",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "#4f46e5" }}>{record.month}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            {record.paymentDate ? dayjs(record.paymentDate).format("DD MMM YYYY") : '-'}
          </div>
        </div>
      )
    },
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>
    },
    {
      title: "Breakdown",
      key: "breakdown",
      render: (_, record) => (
        <div style={{ fontSize: 11, color: "#475569" }}>
          <div>Base: ₹{record.baseSalary?.toLocaleString()}</div>
          {record.allowances > 0 && <div>Allow: ₹{record.allowances?.toLocaleString()}</div>}
          {record.bonus > 0 && <div style={{ color: "green" }}>Bonus: +₹{record.bonus?.toLocaleString()}</div>}
          {record.deductions > 0 && <div style={{ color: "red" }}>Deductions: -₹{record.deductions?.toLocaleString()}</div>}
        </div>
      )
    },
    {
      title: "Net Paid Amount",
      dataIndex: "netAmount",
      key: "netAmount",
      render: (val) => <span style={{ fontWeight: 700, fontSize: 15, color: "#059669" }}>₹{Number(val || 0).toLocaleString()}</span>
    },
    {
      title: "Payment Mode / UTR",
      key: "mode",
      render: (_, record) => (
        <div>
          <Tag color="purple">{String(record.paymentMode || 'bank_transfer').toUpperCase()}</Tag>
          {record.utrNumber && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>UTR: {record.utrNumber}</div>}
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (st, record) => (
        <Tag color={st === 'credited' ? 'green' : 'orange'}>
          {st === 'credited' ? <CheckCircleOutlined style={{ marginRight: 4 }} /> : <ClockCircleOutlined style={{ marginRight: 4 }} />}
          {st ? st.toUpperCase() : 'PENDING'}
        </Tag>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        if (record.status === "pending") {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setSelectedPendingPayment(record);
                creditForm.setFieldsValue({ utrNumber: "" });
                setCreditModalVisible(true);
              }}
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            >
              Mark Credited
            </Button>
          );
        }
        return <Text type="secondary" style={{ fontSize: 12 }}>Completed</Text>;
      }
    }
  ];

  return (
    <div className="salary-accounts-management">
      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Total Base Payroll / Mo</span>}
              value={totalBasePayroll}
              prefix={<BankOutlined style={{ color: '#4f46e5' }} />}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: '#1e293b' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Total Credited Salaries</span>}
              value={creditedThisMonth}
              prefix={<CheckCircleOutlined style={{ color: '#10b981' }} />}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: '#10b981' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Pending Salaries ({pendingPaymentsList.length})</span>}
              value={pendingTotalAmount}
              prefix={<ClockCircleOutlined style={{ color: '#f59e0b' }} />}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: '#f59e0b' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Employee Salary Accounts</span>}
              value={salaryAccounts.length}
              prefix={<UserOutlined style={{ color: '#3b82f6' }} />}
              valueStyle={{ fontWeight: 700, fontSize: 20, color: '#3b82f6' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Action Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Employee Salary Accounts & Payroll</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Manage staff salary profiles, record credited salaries, and process pending payroll</Text>
        </div>

        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAccount(null);
              accountForm.resetFields();
              setAccountModalVisible(true);
            }}
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
          >
            Add Salary Account
          </Button>

          <Button
            icon={<DollarCircleOutlined />}
            onClick={() => {
              paymentForm.resetFields();
              setPaymentModalVisible(true);
            }}
            style={{ borderColor: "#059669", color: "#059669" }}
          >
            Record Salary Payment
          </Button>
        </Space>
      </div>

      {/* Pending Salaries Alert Box */}
      {pendingPaymentsList.length > 0 && (
        <Card
          title={
            <Space>
              <ClockCircleOutlined style={{ color: "#d97706" }} />
              <span style={{ fontWeight: 700, color: "#b45309" }}>
                Pending Salaries Requiring Disbursement ({pendingPaymentsList.length})
              </span>
            </Space>
          }
          style={{ marginBottom: 24, border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 12 }}
        >
          <Table
            dataSource={pendingPaymentsList}
            columns={paymentColumns}
            rowKey="_id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Sub-Tabs: Employee Accounts & Payment History */}
      <Card style={{ borderRadius: 12 }}>
        <Tabs
          items={[
            {
              key: "accounts",
              label: (
                <span>
                  <UserOutlined /> Employee Salary Accounts ({salaryAccounts.length})
                </span>
              ),
              children: (
                <Table
                  columns={accountColumns}
                  dataSource={salaryAccounts}
                  rowKey="_id"
                  loading={loading}
                  pagination={{ pageSize: 8 }}
                />
              )
            },
            {
              key: "history",
              label: (
                <span>
                  <FileTextOutlined /> Salary Payment & Disbursement History ({salaryPayments.length})
                </span>
              ),
              children: (
                <Table
                  columns={paymentColumns}
                  dataSource={salaryPayments}
                  rowKey="_id"
                  loading={loading}
                  pagination={{ pageSize: 8 }}
                />
              )
            }
          ]}
        />
      </Card>

      {/* MODAL 1: Add/Edit Salary Account */}
      <Modal
        title={editingAccount ? "Edit Employee Salary Account" : "Add Employee Salary Account"}
        open={accountModalVisible}
        onCancel={() => {
          setAccountModalVisible(false);
          setEditingAccount(null);
        }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={accountForm} layout="vertical" onFinish={handleSaveAccount}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employeeName" label="Employee Full Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Ramesh Verma" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="designation" label="Designation / Role" initialValue="Sales Agent">
                <Select>
                  <Option value="Sales Agent">Sales Agent</Option>
                  <Option value="Tour Manager">Tour Manager</Option>
                  <Option value="Operations Executive">Operations Executive</Option>
                  <Option value="Accountant">Accountant</Option>
                  <Option value="Team Lead">Team Lead</Option>
                  <Option value="Driver / Transport">Driver / Transport</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email Address">
                <Input placeholder="ramesh@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone Number">
                <Input placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="baseSalary" label="Monthly Base Salary (₹)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={0} placeholder="25000" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="allowances" label="Allowances / HRA (₹)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="5000" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }}>Employee Bank Details</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bankName" label="Bank Name">
                <Input placeholder="HDFC Bank" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="accountNumber" label="Account Number">
                <Input placeholder="50100012345678" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="ifscCode" label="IFSC Code">
                <Input placeholder="HDFC0001234" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="panNumber" label="PAN Card Number">
                <Input placeholder="ABCDE1234F" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="upiId" label="UPI ID">
                <Input placeholder="ramesh@upi" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => setAccountModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#4f46e5", borderColor: "#4f46e5" }}>
              {editingAccount ? "Update Salary Profile" : "Create Salary Profile"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL 2: Record Salary Payment (Credited vs Pending) */}
      <Modal
        title="Record Salary Payment / Disbursement"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        width={650}
        destroyOnClose
      >
        <Form form={paymentForm} layout="vertical" onFinish={handleRecordPayment}>
          <Form.Item name="salaryAccountId" label="Select Employee Salary Profile" rules={[{ required: true }]}>
            <Select
              placeholder="Choose employee"
              onChange={(accId) => {
                const acc = salaryAccounts.find(a => a._id === accId);
                if (acc) {
                  paymentForm.setFieldsValue({
                    employeeName: acc.employeeName,
                    baseSalary: acc.baseSalary,
                    allowances: acc.allowances
                  });
                }
              }}
            >
              {salaryAccounts.map(a => (
                <Option key={a._id} value={a._id}>
                  {a.employeeName} - {a.designation} (Base: ₹{a.baseSalary})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="month" label="Salary Month" initialValue={dayjs().format("MMMM YYYY")} rules={[{ required: true }]}>
                <Input placeholder="e.g. August 2026" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentDate" label="Payment Date" initialValue={dayjs()}>
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="baseSalary" label="Base Salary (₹)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="allowances" label="Allowances (₹)">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bonus" label="Performance Bonus (₹)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="deductions" label="Deductions / Advance (₹)">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="paymentMode" label="Payment Method" initialValue="bank_transfer">
                <Select>
                  <Option value="bank_transfer">Direct Bank Transfer</Option>
                  <Option value="upi">UPI Payment</Option>
                  <Option value="cheque">Cheque</Option>
                  <Option value="cash">Cash</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="utrNumber" label="UTR / Transaction Ref No.">
                <Input placeholder="UTR123456789" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Salary Status" initialValue="credited">
                <Select>
                  <Option value="credited">✅ Credited (Paid)</Option>
                  <Option value="pending">⏳ Pending Disbursement</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="notes" label="Notes">
                <Input placeholder="Optional remark" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 12 }}>
            <Button onClick={() => setPaymentModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#059669", borderColor: "#059669" }}>
              Save Salary Record
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL 3: Quick Mark Credited */}
      <Modal
        title={`Mark Salary Credited - ${selectedPendingPayment?.employeeName}`}
        open={creditModalVisible}
        onCancel={() => setCreditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={creditForm} layout="vertical" onFinish={handleQuickCredit}>
          <p>Confirming payment disbursement of <strong>₹{selectedPendingPayment?.netAmount?.toLocaleString()}</strong> for <strong>{selectedPendingPayment?.month}</strong>.</p>
          <Form.Item name="utrNumber" label="UTR / Transaction Reference Number" rules={[{ required: true, message: "Please enter UTR number" }]}>
            <Input placeholder="e.g. UTR987654321" />
          </Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => setCreditModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#4f46e5", borderColor: "#4f46e5" }}>
              Confirm Salary Credited
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SalaryAccountsManagement;
